import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Avatar,
  Badge,
  Button,
  Empty,
  Input,
  Popconfirm,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  CloseOutlined,
  FlagOutlined,
  MessageOutlined,
  SendOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../contexts/AuthContext";
import profileIcon from "../assets/profile.png";
import {
  ChatMessage,
  getChatId,
  getOtherMemberId,
  isChatUnread,
  MarkChatRead,
  MAX_MESSAGE_LENGTH,
  PublicProfile,
  ReportChatMessage,
  SendChatMessage,
} from "../firebase-chat";
import {
  useChatMessages,
  useMyChats,
  usePublicProfiles,
} from "../hooks/useChat";

const formatTime = (date: Date | null) => {
  if (!date) return "";
  const d = dayjs(date);
  if (d.isSame(dayjs(), "day")) return d.format("HH:mm");
  if (d.isSame(dayjs().subtract(1, "day"), "day"))
    return `Ayer ${d.format("HH:mm")}`;
  return d.format("DD/MM HH:mm");
};

const fabStyle: React.CSSProperties = {
  position: "fixed",
  right: 24,
  bottom: 24,
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "var(--dashboard-header-blue)",
  color: "#fff",
  border: "none",
  boxShadow: "0 10px 24px rgba(10,19,53,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 1000,
  fontSize: 22,
};

const panelStyle: React.CSSProperties = {
  position: "fixed",
  right: 24,
  bottom: 92,
  width: 360,
  maxWidth: "calc(100vw - 32px)",
  height: 520,
  maxHeight: "calc(100vh - 140px)",
  borderRadius: 20,
  background: "var(--dashboard-panel-bg)",
  boxShadow: "0 20px 60px rgba(10,19,53,0.35)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  zIndex: 1000,
  border: "1px solid var(--dashboard-panel-border)",
};

const ProfileAvatar: React.FC<{ profile?: PublicProfile; size?: number }> = ({
  profile,
  size = 36,
}) => (
  <Avatar
    size={size}
    src={profile?.PortraitImg || profileIcon}
    style={{ flexShrink: 0, background: "#fff4c4" }}
  />
);

const ChatWidget: React.FC = () => {
  const { user } = useAuth();
  const myUid = user?.uid ?? "";

  const { chats, unreadCount, isLoading: chatsLoading } = useMyChats(myUid);
  const { profiles, byId, isLoading: profilesLoading } = usePublicProfiles();

  const [open, setOpen] = useState(false);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const chatId = myUid && selectedUid ? getChatId(myUid, selectedUid) : null;
  const currentChat = chats.find((c) => c.id === chatId) ?? null;
  const otherProfile = selectedUid ? byId.get(selectedUid) : undefined;
  const { messages, isLoading: messagesLoading } = useChatMessages(chatId);

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return profiles.filter(
      (p) => p.id !== myUid && p.Nombre.toLowerCase().includes(q),
    );
  }, [profiles, search, myUid]);

  useEffect(() => {
    if (!open || !currentChat || !myUid) return;
    if (isChatUnread(currentChat, myUid)) {
      MarkChatRead(currentChat.id, myUid).catch((error) =>
        console.error("No se pudo marcar como leído:", error),
      );
    }
  }, [open, currentChat, myUid]);

  const scrollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, selectedUid]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !selectedUid || sending) return;
    setSending(true);
    try {
      await SendChatMessage({
        myUid,
        otherUid: selectedUid,
        text,
        chatExists: Boolean(currentChat),
      });
      setDraft("");
    } catch (error) {
      console.error(error);
    } finally {
      setSending(false);
    }
  };

  const handleReport = async (msg: ChatMessage) => {
    if (!chatId) return;
    try {
      await ReportChatMessage({ chatId, message: msg, myUid });
    } catch (error) {
      console.error(error);
    }
  };

  if (!myUid) return null;

  return (
    <>
      <button
        type="button"
        style={fabStyle}
        onClick={() => setOpen((prev) => !prev)}
        aria-label={open ? "Cerrar chat" : "Abrir chat"}
      >
        <Badge count={unreadCount} size="small" offset={[-2, 2]}>
          <MessageOutlined style={{ color: "#fff", fontSize: 22 }} />
        </Badge>
      </button>

      {open && (
        <div style={panelStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 14px",
              borderBottom: "1px solid var(--dashboard-panel-border)",
              background: "var(--dashboard-header-blue)",
            }}
          >
            {selectedUid && (
              <Button
                shape="circle"
                size="small"
                icon={<ArrowLeftOutlined />}
                onClick={() => setSelectedUid(null)}
                aria-label="Volver a la lista"
              />
            )}
            {selectedUid && <ProfileAvatar profile={otherProfile} size={30} />}
            <Typography.Text
              strong
              style={{ color: "#fff", flex: 1, minWidth: 0 }}
              ellipsis
            >
              {selectedUid ? (otherProfile?.Nombre ?? "Alumno") : "Mensajes"}
            </Typography.Text>
            <Button
              shape="circle"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
              aria-label="Cerrar chat"
            />
          </div>

          {!selectedUid ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                padding: 12,
                minHeight: 0,
              }}
            >
              <Input.Search
                placeholder="Buscar compañero por nombre"
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div style={{ flex: 1, overflowY: "auto", margin: "0 -4px" }}>
                {search.trim() ? (
                  profilesLoading ? (
                    <Spin style={{ display: "block", marginTop: 24 }} />
                  ) : searchResults.length === 0 ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Nadie coincide con ese nombre."
                    />
                  ) : (
                    searchResults.map((p) => (
                      <ChatListRow
                        key={p.id}
                        profile={p}
                        subtitle={`Nivel ${p.Nivel}`}
                        onClick={() => {
                          setSelectedUid(p.id);
                          setSearch("");
                        }}
                      />
                    ))
                  )
                ) : chatsLoading ? (
                  <Spin style={{ display: "block", marginTop: 24 }} />
                ) : chats.length === 0 ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Todavía no tenés conversaciones. Buscá a un compañero."
                  />
                ) : (
                  chats.map((c) => {
                    const otherId = getOtherMemberId(c, myUid);
                    const profile = byId.get(otherId);
                    const mine = c.UltimoAutorId === myUid;
                    return (
                      <ChatListRow
                        key={c.id}
                        profile={profile}
                        subtitle={`${mine ? "Vos: " : ""}${c.UltimoMensaje}`}
                        time={formatTime(c.ActualizadoEl)}
                        unread={isChatUnread(c, myUid)}
                        onClick={() => setSelectedUid(otherId)}
                      />
                    );
                  })
                )}
              </div>
            </div>
          ) : (
            <>
              <div
                ref={scrollRef}
                style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: "12px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minHeight: 0,
                }}
              >
                {messagesLoading && messages.length === 0 ? (
                  <Spin style={{ margin: "auto" }} />
                ) : messages.length === 0 ? (
                  <Typography.Text
                    style={{
                      margin: "auto",
                      color: "var(--dashboard-text)",
                      textAlign: "center",
                    }}
                  >
                    Mandale el primer mensaje a{" "}
                    {otherProfile?.Nombre ?? "tu compañero"}.
                  </Typography.Text>
                ) : (
                  messages.map((m) => (
                    <ChatMessageBubble
                      key={m.id}
                      message={m}
                      mine={m.AutorId === myUid}
                      onReport={() => handleReport(m)}
                    />
                  ))
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-end",
                  padding: 10,
                  borderTop: "1px solid var(--dashboard-panel-border)",
                }}
              >
                <Input.TextArea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                  placeholder="Escribí un mensaje"
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  maxLength={MAX_MESSAGE_LENGTH}
                  style={{ borderRadius: 16 }}
                />
                <Button
                  type="primary"
                  shape="circle"
                  icon={<SendOutlined />}
                  onClick={() => void handleSend()}
                  loading={sending}
                  disabled={!draft.trim()}
                  aria-label="Enviar mensaje"
                />
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

const ChatListRow: React.FC<{
  profile?: PublicProfile;
  subtitle: string;
  time?: string;
  unread?: boolean;
  onClick: () => void;
}> = ({ profile, subtitle, time, unread, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
      padding: "8px 6px",
      border: 0,
      borderRadius: 12,
      cursor: "pointer",
      textAlign: "left",
      font: "inherit",
      background: "transparent",
    }}
  >
    <Badge dot={unread} offset={[-2, 2]}>
      <ProfileAvatar profile={profile} />
    </Badge>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span
          style={{
            fontWeight: unread ? 700 : 600,
            color: "var(--dashboard-header-blue)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {profile?.Nombre ?? "Alumno"}
        </span>
        {time && (
          <span
            style={{
              fontSize: 10,
              color: "var(--dashboard-text)",
              flexShrink: 0,
            }}
          >
            {time}
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "var(--dashboard-text)",
          fontWeight: unread ? 600 : 400,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {subtitle}
      </div>
    </div>
  </button>
);

const ChatMessageBubble: React.FC<{
  message: ChatMessage;
  mine: boolean;
  onReport: () => void;
}> = ({ message, mine, onReport }) => (
  <div
    style={{
      display: "flex",
      justifyContent: mine ? "flex-end" : "flex-start",
      alignItems: "flex-end",
      gap: 4,
    }}
  >
    <div
      style={{
        maxWidth: "80%",
        padding: "6px 10px",
        borderRadius: 14,
        borderBottomRightRadius: mine ? 4 : 14,
        borderBottomLeftRadius: mine ? 14 : 4,
        background: mine
          ? "var(--dashboard-header-blue)"
          : "var(--dashboard-panel-soft)",
        color: mine ? "var(--dashboard-white)" : "var(--dashboard-text)",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontSize: 13,
      }}
    >
      {message.Texto}
      <div
        style={{ fontSize: 9, opacity: 0.7, textAlign: "right", marginTop: 2 }}
      >
        {formatTime(message.Fecha)}
      </div>
    </div>
    {!mine && (
      <Popconfirm
        title="¿Reportar este mensaje?"
        okText="Reportar"
        cancelText="Cancelar"
        onConfirm={onReport}
      >
        <Tooltip title="Reportar">
          <Button
            type="text"
            size="small"
            icon={<FlagOutlined />}
            aria-label="Reportar mensaje"
            style={{ opacity: 0.45 }}
          />
        </Tooltip>
      </Popconfirm>
    )}
  </div>
);

export default ChatWidget;
